import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Tag } from "lucide-react";

export const ArticleLayout = ({ tag, title, readTime, intro, image, children, testId }) => {
  return (
    <article
      data-testid={testId}
      className="relative pt-28 pb-14 md:pt-32 md:pb-16 bg-[#0E2452] border-t border-[#2F568F]"
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link
            to="/guias"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#FF5A00] font-display text-xs uppercase tracking-widest mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar aos Guias
          </Link>

          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#FF5A00] font-display font-bold">
              <Tag className="w-3 h-3" />
              {tag}
            </span>
            <span className="text-[#5A7AAE]">·</span>
            <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] font-display">
              <Clock className="w-3 h-3" />
              {readTime} de leitura
            </span>
          </div>

          <h1 className="font-display font-bold text-white text-3xl md:text-4xl uppercase tracking-tight leading-[1.05] mb-5">
            {title}
          </h1>

          {intro && (
            <p
              data-testid="article-intro"
              className="text-[#A1A1AA] font-body text-lg md:text-xl leading-relaxed mb-10"
            >
              {intro}
            </p>
          )}

          {image && (
            <div className="bg-[#163A6D] border border-[#2F568F] p-6 md:p-10 mb-10 flex items-center justify-center">
              <img
                src={image}
                alt={title}
                className="max-h-72 md:max-h-80 object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose-article space-y-6 text-[#E0E0E0] font-body text-base md:text-[17px] leading-relaxed">
            {children}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-[#FF5A00] to-[#FF7A2D] p-6 md:p-8 text-black flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <div className="font-display font-bold uppercase text-xl md:text-2xl leading-tight">
                Precisa de aconselhamento personalizado?
              </div>
              <p className="text-black/80 font-body text-sm mt-1">
                Diga-nos o seu carro e nós recomendamos a melhor opção.
              </p>
            </div>
            <Link
              to="/orcamento"
              data-testid="article-cta-orcamento"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-black text-white font-display font-bold uppercase tracking-wider text-sm hover:bg-[#0E2452] transition-colors whitespace-nowrap"
            >
              Pedir Orçamento
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};

/** Heading + paragraph helpers to keep article JSX readable */
export const H2 = ({ children }) => (
  <h2 className="font-display font-bold text-white text-2xl md:text-3xl uppercase tracking-tight mt-12 mb-4">
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="font-display font-bold text-[#FF5A00] text-lg md:text-xl uppercase tracking-tight mt-8 mb-3">
    {children}
  </h3>
);

export const Quote = ({ children }) => (
  <blockquote className="border-l-2 border-[#FF5A00] pl-5 my-8 text-white font-display text-lg md:text-xl uppercase tracking-tight leading-snug">
    {children}
  </blockquote>
);

export const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto my-8 border border-[#2F568F]">
    <table className="w-full text-sm md:text-[15px] font-body">
      <thead>
        <tr className="bg-[#163A6D] text-white">
          {headers.map((h) => (
            <th
              key={h}
              className="px-4 py-3 text-left font-display uppercase text-[11px] tracking-widest border-b border-[#2F568F]"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr
            key={row[0] || ri}
            className="even:bg-[#163A6D]/30 text-[#E0E0E0]"
          >
            {row.map((cell, ci) => (
              <td
                key={ci}
                className="px-4 py-3 border-t border-[#2F568F]/60 align-top"
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ArticleLayout;
